/**
 * Frameium — "Similar from Pinterest" poster rail on the product page.
 *
 * Two calls, in order:
 *
 *   /api/pin/search?query=<product title>&limit=25
 *     The product's own title is the query. Its results normally only produce
 *     the seeds for the call below — but they are also what the rail falls back
 *     to when that call comes back empty or fails.
 *
 *   /api/pin/similar?id=<first 4 ids>&exclude=<all ids>&limit=20
 *     `id` is the seed set, `exclude` is everything the search already turned
 *     up so the rail can't hand back the same pins the seeds came from. Later
 *     pages pass the returned `bookmark` instead of `exclude`, which is what
 *     carries the cursor.
 *
 * Those two requests take seconds, so the rail reveals itself as soon as the
 * first one is sent, filled with shimmering skeleton cards, and swaps them for
 * real ones when the images arrive — it is never a section that appears out of
 * nowhere late. MAX_CARDS below is a hard 20-card ceiling on the whole rail, so
 * a single similar page is normally all it ever asks for.
 *
 * Titles that are numeric or shorter than three characters are dropped before
 * either call: "2024" and "A4" are sizes and years, and searching them returns
 * a rail of unrelated posters. The section stays hidden in that case, and in
 * every case where the two calls end up with nothing to show.
 *
 * Cards link to the "Find your poster" product carrying the signed image params
 * (p + k), so the tapped image is already applied on arrival — the same
 * fpp_* contract assets/fm-poster-search.js uses for the search page.
 *
 * The API base, the poster product and its price all come from [data-fm-ps]
 * (snippets/fm-poster-search.liquid), which theme.liquid renders on every page.
 * Without it there is nowhere for the cards to link, so the rail stays hidden.
 */
(function () {
  'use strict';

  var MAX_PAGES = 4;

  // Hard ceiling on the rail — 20 cards and not one more, however the section
  // is configured. Paging stops the moment it's reached: every extra card is
  // another framed render generated on demand, and nobody scrolls a rail that
  // far.
  var MAX_CARDS = 20;

  /* ────────────────────────────── helpers ────────────────────────────── */

  function paramOf(url, name) {
    var m = new RegExp('[?&]' + name + '=([^&]*)').exec(url || '');
    return m ? decodeURIComponent(m[1]) : '';
  }

  function withQuery(base, params) {
    var parts = [];
    for (var k in params) {
      if (params[k] === '' || params[k] == null) continue;
      parts.push(k + '=' + encodeURIComponent(params[k]));
    }
    return base + (base.indexOf('?') === -1 ? '?' : '&') + parts.join('&');
  }

  // Pin titles are third-party text and go in as markup, unlike the customer's
  // own query on the search page.
  function esc(text) {
    return String(text == null ? '' : text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // Worth searching? Anything made only of digits and the separators that go
  // with them — "2024", "50", "12 x 18", "A/W" is fine but "1.5" is not — is a
  // size, a year or a count rather than a subject.
  function searchable(text, minChars) {
    var t = (text || '').trim();
    if (t.length < minChars) return false;
    return /[^\d\s.,\-x×/\\]/i.test(t);
  }

  // The framed render, so a card reads as a product rather than a stray picture.
  // Framing only exists at mid size, but the signature covers the image id and
  // not the size, so the thumb of the same framed render is safe and lighter.
  function framedThumb(im) {
    if (im.framed) return im.framed.replace('s=mid', 's=thumb');
    return im.thumb || im.image;
  }

  function getJSON(url) {
    return fetch(url).then(function (r) {
      if (!r.ok) throw new Error('fm-sim ' + r.status);
      return r.json();
    });
  }

  /* ─────────────────────────── shared config ─────────────────────────── */

  // API base, poster product, variant and price — all already rendered once per
  // page for the store search. Read rather than re-rendered so the price logic
  // lives in exactly one place.
  function posterConfig() {
    var cfg = document.querySelector('[data-fm-ps]');
    if (!cfg) return null;
    var api = (cfg.getAttribute('data-api') || '').replace(/\/+$/, '');
    var product = cfg.getAttribute('data-product') || '';
    if (!api || !product) return null;
    return {
      api: api,
      product: product,
      variant: cfg.getAttribute('data-variant') || '',
      price: cfg.getAttribute('data-price') || '',
      compare: cfg.getAttribute('data-compare') || ''
    };
  }

  /* ─────────────────────────────── one rail ─────────────────────────────── */

  function init(root) {
    if (root.hasAttribute('data-fm-sim-init')) return;
    root.setAttribute('data-fm-sim-init', '1');

    var cfg = posterConfig();
    if (!cfg) return;

    var query = (root.getAttribute('data-query') || '').trim();
    var minChars = parseInt(root.getAttribute('data-min-chars'), 10) || 3;
    if (!searchable(query, minChars)) return;

    var SEARCH_LIMIT = parseInt(root.getAttribute('data-search-limit'), 10) || 25;
    var SEED_COUNT = parseInt(root.getAttribute('data-seed-count'), 10) || 4;
    var LIMIT = parseInt(root.getAttribute('data-limit'), 10) || MAX_CARDS;
    if (LIMIT > MAX_CARDS) LIMIT = MAX_CARDS;

    var row = root.querySelector('[data-fm-sim-row]');
    if (!row) return;

    var seeds = '';
    var bookmark = null;
    var pages = 0;
    var loading = false;
    var done = false;
    var seen = {};
    // Cards ever appended — what the MAX_CARDS ceiling and the paging counter
    // are about. How many are on screen right now is a different number (a card
    // whose render never arrives is removed), and asking the DOM for that is the
    // only way to keep the two from drifting apart: a counter that both the
    // append loop and the image error handler adjust went negative mid-loop the
    // first time a batch of renders failed from cache, and hid a rail that still
    // had most of its cards.
    var appended = 0;

    function cardCount() {
      return row.querySelectorAll('a.fm-sim__card').length;
    }

    /* ── cards ── */

    function priceMarkup() {
      var html = '';
      if (cfg.compare) html += '<span class="compare-at-price">' + esc(cfg.compare) + '</span> ';
      if (cfg.price) html += '<span class="price">' + esc(cfg.price) + '</span>';
      return html;
    }

    function pinLink(im) {
      return withQuery(cfg.product, {
        // Without this the product opens on its first variant, the smallest size.
        variant: cfg.variant,
        fpp_q: query,
        fpp_pin: im.id || '',
        fpp_p: im.ref || paramOf(im.image, 'p'),
        fpp_k: paramOf(im.image, 'k'),
        fpp_w: im.width || '',
        fpp_h: im.height || '',
        fpp_t: (im.title || '').slice(0, 90)
      });
    }

    // Each framed render is generated on demand, so a card can sit empty for
    // seconds after its markup exists — keep the shimmer until the image paints
    // rather than only until the request returns.
    //
    // A render that never arrives takes its card with it — but only on the
    // second try. The endpoint answers a render it hasn't finished with a short
    // non-image body, which is a "come back in a moment", not a dead image, so
    // the first failure buys one retry a second later. A card that fails twice
    // is dropped: it would otherwise sit there as an empty box under a title and
    // a price, which reads as a broken product. The rail is better one short.
    function trackImage(card) {
      var media = card.querySelector('[data-fm-sim-media]');
      var img = card.querySelector('img');
      if (!media || !img) return;
      media.classList.add('fm-sim__loading');
      var retried = false;

      function stop() {
        img.removeEventListener('load', onLoad);
        img.removeEventListener('error', onError);
      }
      function onLoad() {
        stop();
        media.classList.remove('fm-sim__loading');
      }
      function onError() {
        stop();
        if (!retried) {
          retried = true;
          var src = img.getAttribute('src');
          setTimeout(function () {
            img.addEventListener('load', onLoad);
            img.addEventListener('error', onError);
            img.setAttribute('src', src);
          }, 1000);
          return;
        }
        if (card.parentNode) card.parentNode.removeChild(card);
        // Only when the rail has been emptied out entirely, and never while a
        // request that could refill it is still in the air.
        if (!cardCount() && !loading) root.hidden = true;
      }

      if (img.complete) {
        if (img.naturalWidth) onLoad();
        else onError();
        return;
      }
      img.addEventListener('load', onLoad);
      img.addEventListener('error', onError);
    }

    // A slide, not a bare card: .resource-list__slide is what the theme's own
    // carousel puts around each card, and it carries the slide-width rules that
    // keep these cards the same size as the ones in "You may also like".
    function slide(inner, extraClass) {
      var el = document.createElement('div');
      el.className = 'resource-list__slide' + (extraClass ? ' ' + extraClass : '');
      el.innerHTML = inner;
      return el;
    }

    // Title and price are the theme's own .text-block typography — .rte for the
    // title and .h6 for the price — because that is what the recommendations
    // card blocks resolve to. Copying the classes rather than the computed
    // values keeps the two cards identical if the type presets ever change.
    function card(im) {
      var el = slide(
        '<a class="fm-sim__card product-card" href="' + esc(pinLink(im)) + '">' +
          '<span class="fm-sim__media" data-fm-sim-media>' +
          '<img src="' + esc(framedThumb(im)) + '" alt="" loading="lazy" decoding="async">' +
          '</span>' +
          // The product's name, not the pin's. Pinterest titles are scraped
          // image captions — "two posters are hanging on the wall next to each
          // other" — and read as junk on a store page. The pin's own title still
          // travels on the link as fpp_t, so it lands on the order. Same as the
          // search page, where every card is titled with what was searched.
          '<div class="text-block rte fm-sim__title"><p>' + esc(query) + '</p></div>' +
          '<div class="text-block h6 fm-sim__price" style="--color: var(--color-foreground);">' +
          priceMarkup() +
          '</div>' +
          '</a>'
      );
      return el;
    }

    /* ── skeletons ── */

    function showSkeletons(count) {
      clearSkeletons();
      for (var i = 0; i < count; i++) {
        var el = slide(
          '<span class="fm-sim__card">' +
            '<span class="fm-sim__media fm-sim__loading"></span>' +
            '<span class="fm-sim__skel-line"></span>' +
            '<span class="fm-sim__skel-line fm-sim__skel-line--short"></span>' +
            '</span>'
        );
        el.setAttribute('data-fm-sim-skeleton', '');
        row.appendChild(el);
      }
    }

    function clearSkeletons() {
      var skels = row.querySelectorAll('[data-fm-sim-skeleton]');
      for (var i = 0; i < skels.length; i++) skels[i].parentNode.removeChild(skels[i]);
    }

    /* ── requests ── */

    // Page one. The search names the seeds and fills the exclude list — and it
    // is also the rail's safety net: "more like this" is a second request that
    // can come back empty or fail outright, and now that the section reveals
    // itself the moment it starts loading, that would show a heading and a row
    // of shimmering cards and then take the whole thing away a few seconds
    // later. The search results are already in hand and are images of the
    // product's own title, so they stand in whenever the similar call has
    // nothing. A less varied rail beats one that vanishes.
    function firstPage() {
      return getJSON(cfg.api + '/api/pin/search?query=' + encodeURIComponent(query) + '&limit=' + SEARCH_LIMIT).then(
        function (data) {
          var found = data.results || [];
          var ids = [];
          for (var i = 0; i < found.length; i++) {
            if (found[i] && found[i].id) ids.push(found[i].id);
          }
          if (!ids.length) return { results: [], bookmark: null };

          // No cursor on the fallback: the search page is a single page, and a
          // bookmark from the similar endpoint doesn't belong to it.
          var fallback = { results: found, bookmark: null };

          seeds = ids.slice(0, SEED_COUNT).join(',');
          var params = new URLSearchParams();
          params.set('id', seeds);
          params.set('exclude', ids.join(','));
          params.set('limit', String(LIMIT));
          return getJSON(cfg.api + '/api/pin/similar?' + params.toString())
            .then(function (sim) {
              return sim && sim.results && sim.results.length ? sim : fallback;
            })
            .catch(function () {
              return fallback;
            });
        }
      );
    }

    // Later pages: same seeds plus the cursor. `exclude` is dropped on purpose —
    // the bookmark already encodes where the previous page stopped, and the list
    // would only keep growing.
    function nextPage() {
      var params = new URLSearchParams();
      params.set('id', seeds);
      params.set('bookmark', bookmark);
      params.set('limit', String(LIMIT));
      return getJSON(cfg.api + '/api/pin/similar?' + params.toString());
    }

    function absorb(data) {
      var list = (data && data.results) || [];
      bookmark = (data && data.bookmark) || null;

      var added = 0;
      for (var i = 0; i < list.length; i++) {
        if (appended >= MAX_CARDS) break;
        var im = list[i];
        // Pages can overlap, and a repeated pin would render as a duplicate card.
        if (!im || (im.id && seen[im.id])) continue;
        if (im.id) seen[im.id] = true;
        var el = card(im);
        row.appendChild(el);
        appended++;
        added++;
        // After the count, never before: a render already cached as a failure
        // errors synchronously from here.
        trackImage(el);
      }
      if (!bookmark || !list.length || appended >= MAX_CARDS) done = true;
      return added;
    }

    function load(first) {
      if (loading || done) return;
      loading = true;
      pages++;
      // Show the rail the moment it starts working, as a row of shimmering
      // cards, instead of leaving it display:none through two chained requests
      // and popping the whole section in seconds later. It puts itself back
      // hidden below if the requests end up with nothing to show.
      if (first) root.hidden = false;
      showSkeletons(first ? Math.min(LIMIT, 6) : 3);

      (first ? firstPage() : nextPage())
        .then(function (data) {
          loading = false;
          clearSkeletons();
          absorb(data);
          root.hidden = !cardCount();
          if (pages >= MAX_PAGES) done = true;
        })
        .catch(function () {
          // Don't keep hammering a failing endpoint on every scroll.
          loading = false;
          done = true;
          clearSkeletons();
          if (!cardCount()) root.hidden = true;
        });
    }

    /* ── paging on scroll of the rail itself ── */

    row.addEventListener(
      'scroll',
      function () {
        if (done || loading || !cardCount()) return;
        var remaining = row.scrollWidth - Math.abs(row.scrollLeft) - row.clientWidth;
        if (remaining < row.clientWidth) load(false);
      },
      { passive: true }
    );

    /* ── start ── */

    // Deferred until the rail is close to the viewport: the product page has its
    // own images to fetch first, and this is two chained requests below the fold.
    // Watched on the anchor, never on the section — the section is display:none
    // until it has cards, and a display:none element never intersects, so an
    // observer pointed at it would sit silent and the rail would never load.
    var anchor = document.getElementById(root.getAttribute('data-anchor') || '');

    function start() {
      load(true);
    }

    if (anchor && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          if (!entries[0] || !entries[0].isIntersecting) return;
          io.disconnect();
          start();
        },
        // Generous, because the two requests behind this take 3–6 seconds
        // between them: start them a screenful and a half early and the cards
        // are often already there by the time the rail is scrolled to.
        { rootMargin: '1500px 0px' }
      );
      io.observe(anchor);
    } else {
      start();
    }
  }

  function initAll() {
    var rails = document.querySelectorAll('[data-fm-sim]');
    for (var i = 0; i < rails.length; i++) init(rails[i]);
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
})();
